// Carrito y armado de ventas y devoluciones. Funciones puras (testeables sin navegador):
// las cuentas son las de core, que el servidor vuelve a comprobar en app.record_sale.
import {
  SaleError,
  computeCart,
  discountPct,
  paymentTotals,
  refundAdjustment,
  refundForLine,
  splitTax,
  type CartTotals,
  type PaymentLine,
  type PaymentMethod,
  type UnitType,
} from '@pos/core';
import type { SaleItemDoc, SalePaymentDoc, SaleRow } from '@pos/local-db';

export interface CartLine {
  variantId: string;
  label: string;
  unitType: UnitType;
  /** Precio unitario al agregarlo (centavos). */
  unitPrice: number;
  qtyMilli: number;
  /** Descuento propio de la línea (centavos). */
  discount: number;
  taxBps: number;
}

export interface Cart {
  lines: CartLine[];
  /** Descuento sobre el total (centavos). */
  globalDiscount: number;
  /** Cliente de la venta (opcional; obligatorio para cuenta corriente). */
  customerId?: string | null;
}

export const EMPTY_CART: Cart = { lines: [], globalDiscount: 0, customerId: null };

export interface Product {
  variantId: string;
  label: string;
  unitType: UnitType;
  price: number;
  taxBps: number;
}

/** Agrega un producto. Si ya estaba (mismo precio), suma la cantidad en esa línea. */
export function addToCart(cart: Cart, product: Product, qtyMilli: number): Cart {
  const index = cart.lines.findIndex((l) => l.variantId === product.variantId && l.unitPrice === product.price);
  if (index >= 0) {
    return { ...cart, lines: cart.lines.map((l, i) => (i === index ? { ...l, qtyMilli: l.qtyMilli + qtyMilli } : l)) };
  }
  const line: CartLine = {
    variantId: product.variantId,
    label: product.label,
    unitType: product.unitType,
    unitPrice: product.price,
    qtyMilli,
    discount: 0,
    taxBps: product.taxBps,
  };
  return { ...cart, lines: [...cart.lines, line] };
}

/** Cambia la cantidad de una línea (0 o menos la quita). El descuento no puede quedar mayor al importe. */
export function setLineQty(cart: Cart, index: number, qtyMilli: number): Cart {
  if (qtyMilli <= 0) return removeLine(cart, index);
  return clampGlobal({
    ...cart,
    lines: cart.lines.map((l, i) => (i === index ? { ...l, qtyMilli, discount: Math.min(l.discount, Math.round((l.unitPrice * qtyMilli) / 1000)) } : l)),
  });
}

export function setLineDiscount(cart: Cart, index: number, discount: number): Cart {
  return clampGlobal({ ...cart, lines: cart.lines.map((l, i) => (i === index ? { ...l, discount } : l)) });
}

export function removeLine(cart: Cart, index: number): Cart {
  return clampGlobal({ ...cart, lines: cart.lines.filter((_, i) => i !== index) });
}

/** Si al sacar productos el descuento general queda más grande que el total, se ajusta. */
function clampGlobal(cart: Cart): Cart {
  const available = cart.lines.reduce((sum, l) => sum + Math.round((l.unitPrice * l.qtyMilli) / 1000) - l.discount, 0);
  return cart.globalDiscount > available ? { ...cart, globalDiscount: Math.max(0, available) } : cart;
}

export function cartTotals(cart: Cart): CartTotals {
  return computeCart(
    cart.lines.map((l) => ({ unitPrice: l.unitPrice, qtyMilli: l.qtyMilli, discount: l.discount, taxBps: l.taxBps })),
    cart.globalDiscount,
  );
}

/** Cantidad de cada variante en el carrito (para avisar si supera el stock). */
export function qtyInCart(cart: Cart, variantId: string): number {
  return cart.lines.reduce((sum, l) => (l.variantId === variantId ? sum + l.qtyMilli : sum), 0);
}

// ---------------------------------------------------------------------------
// Venta
// ---------------------------------------------------------------------------

export interface SaleContext {
  businessId: string;
  deviceId: string | null;
  sellerId: string;
  /** Caja abierta en el dispositivo (null si no hay). */
  cashSessionId?: string | null;
  now: string;
  newId: () => string;
}

const toPaymentDoc = (p: PaymentLine, newId: () => string): SalePaymentDoc => ({
  id: newId(),
  method: p.method,
  base: p.base,
  adjustment: p.adjustment,
  rounding: p.rounding,
  amount: p.amount,
  tendered: p.method === 'cash' ? p.tendered : null,
});

/**
 * Arma la venta lista para guardar. `maxDiscount` es el tope del vendedor en %
 * (100 para el dueño). Los ajustes por medio de pago no cuentan como descuento del vendedor.
 */
export function buildSale(cart: Cart, payments: PaymentLine[], maxDiscount: number, ctx: SaleContext): Omit<SaleRow, 'number'> {
  if (cart.lines.length === 0) throw new SaleError('El carrito está vacío.');
  const totals = cartTotals(cart);
  if (totals.discountTotal > 0 && discountPct(totals.subtotal, totals.discountTotal) > maxDiscount + 1e-9) {
    throw new SaleError(`El descuento supera tu tope de ${maxDiscount} %.`);
  }
  if (payments.length === 0) throw new SaleError('Elegí cómo paga.');
  const paid = paymentTotals(payments);
  if (paid.base !== totals.baseTotal) throw new SaleError('Los pagos no cubren el total.');
  if (payments.some((p) => p.method === 'account') && !cart.customerId) throw new SaleError('Para vender a cuenta corriente elegí un cliente.');

  const items: SaleItemDoc[] = cart.lines.map((l, i) => ({
    id: ctx.newId(),
    movement_id: ctx.newId(),
    variant_id: l.variantId,
    description: l.label.slice(0, 200),
    unit_type: l.unitType,
    qty_milli: l.qtyMilli,
    unit_price: l.unitPrice,
    discount: totals.lines[i]!.discount,
    tax_bps: l.taxBps,
    net: totals.lines[i]!.net,
    tax: totals.lines[i]!.tax,
    total: totals.lines[i]!.total,
  }));
  return {
    id: ctx.newId(),
    business_id: ctx.businessId,
    kind: 'sale',
    original_sale_id: null,
    device_id: ctx.deviceId,
    seller_id: ctx.sellerId,
    subtotal: totals.subtotal,
    discount_total: totals.discountTotal,
    adjustment_total: paid.adjustment,
    rounding_total: paid.rounding,
    tax_total: totals.taxTotal,
    total: paid.total,
    notes: null,
    occurred_at: ctx.now,
    created_at: ctx.now,
    customer_id: cart.customerId ?? null,
    cash_session_id: ctx.cashSessionId ?? null,
    items,
    payments: payments.map((p) => toPaymentDoc(p, ctx.newId)),
  };
}

// ---------------------------------------------------------------------------
// Devolución
// ---------------------------------------------------------------------------

export interface ReturnSelection {
  /** Línea de la venta original. */
  itemId: string;
  qtyMilli: number;
}

/** Lo que falta devolver de cada línea. */
export function returnableQty(original: SaleRow, returned: Map<string, number>): Map<string, number> {
  return new Map(original.items.map((i) => [i.id, i.qty_milli - (returned.get(i.id) ?? 0)]));
}

/**
 * Arma la devolución (total o parcial) de una venta. Devuelve lo cobrado por cada producto y
 * la parte proporcional de los recargos/descuentos por medio de pago. `previousReturns`: las
 * devoluciones ya hechas de esa venta (para no devolver de más ni perder centavos).
 */
export function buildReturn(
  original: SaleRow,
  selections: ReturnSelection[],
  previousReturns: SaleRow[],
  method: PaymentMethod,
  ctx: SaleContext,
): Omit<SaleRow, 'number'> {
  if (original.kind !== 'sale') throw new SaleError('Solo se pueden devolver ventas.');
  if (method === 'account' && !original.customer_id) throw new SaleError('La venta no tiene cliente: no se puede devolver a cuenta corriente.');
  const chosen = selections.filter((s) => s.qtyMilli > 0);
  if (chosen.length === 0) throw new SaleError('Elegí qué productos se devuelven.');
  const returned = new Map<string, number>();
  for (const r of previousReturns) for (const i of r.items) if (i.original_item_id) returned.set(i.original_item_id, (returned.get(i.original_item_id) ?? 0) + i.qty_milli);

  const items: SaleItemDoc[] = chosen.map((s) => {
    const item = original.items.find((i) => i.id === s.itemId);
    if (!item) throw new SaleError('Ese producto no está en la venta.');
    const before = returned.get(item.id) ?? 0;
    const total = refundForLine(item.total, item.qty_milli, before, s.qtyMilli);
    // Importe bruto y descuento proporcionales, para que bruto − descuento = total.
    const gross = Math.round((item.unit_price * s.qtyMilli) / 1000);
    const discount = Math.max(0, gross - total);
    return {
      id: ctx.newId(),
      movement_id: ctx.newId(),
      variant_id: item.variant_id,
      description: item.description,
      unit_type: item.unit_type,
      qty_milli: s.qtyMilli,
      unit_price: item.unit_price,
      discount,
      tax_bps: item.tax_bps,
      ...splitTax(gross - discount, item.tax_bps),
      total: gross - discount,
      original_item_id: item.id,
    };
  });

  const base = items.reduce((a, i) => a + i.total, 0);
  const originalBase = original.subtotal - original.discount_total;
  const previousBase = previousReturns.reduce((a, r) => a + (r.subtotal - r.discount_total), 0);
  const adjustment = refundAdjustment(originalBase, original.adjustment_total + original.rounding_total, previousBase, base);
  const payment: SalePaymentDoc = { id: ctx.newId(), method, base, adjustment, rounding: 0, amount: base + adjustment, tendered: null };
  if (payment.amount < 0) throw new SaleError('El importe a devolver no es válido.');

  return {
    id: ctx.newId(),
    business_id: ctx.businessId,
    kind: 'return',
    original_sale_id: original.id,
    device_id: ctx.deviceId,
    seller_id: ctx.sellerId,
    subtotal: items.reduce((a, i) => a + Math.round((i.unit_price * i.qty_milli) / 1000), 0),
    discount_total: items.reduce((a, i) => a + i.discount, 0),
    adjustment_total: adjustment,
    rounding_total: 0,
    tax_total: items.reduce((a, i) => a + i.tax, 0),
    total: payment.amount,
    notes: null,
    occurred_at: ctx.now,
    created_at: ctx.now,
    customer_id: original.customer_id ?? null,
    cash_session_id: ctx.cashSessionId ?? null,
    items,
    payments: [payment],
  };
}

/** Medio con el que se pagó la mayor parte (sugerido para devolver). */
export function mainMethod(sale: SaleRow): PaymentMethod {
  return [...sale.payments].sort((a, b) => b.amount - a.amount)[0]?.method ?? 'cash';
}
